import { AntSelectProps, AntSelectPropsGeneric, AntSelectValue, IdObject } from 'controls/react/ant/ant-select/types';
import { BaseOptionType } from '@rc-component/select/lib/Select';
import React from 'react';

export function useObjectValue<
	VT extends AntSelectValue,
	VTObj extends IdObject,
	OptionType extends BaseOptionType,
	P extends AntSelectProps<VT, VTObj, OptionType>,
>(props: P): AntSelectPropsGeneric<VT, OptionType> {
	let fields = '';
	if (props.valueAsObject == true) {
		fields = props.objectFields?.join('') ?? '';
	}

	const processedValue = React.useMemo(() => {
		if (props.valueAsObject == true) {
			const valueObject = props.value as { id: string }[];
			return valueObject?.map((x) => x.id) as VT;
		} else {
			return props.value;
		}
	}, [props.value]);

	const onChangeWrapper = React.useCallback(
		(value: VT, option: OptionType | OptionType[]) => {
			if (props.valueAsObject == true) {
				const ids = value as string[];
				const selectedItems = ids
					.map((id) => props.options.find((x) => x.id == id))
					.filter((x) => x)
					.map((item) => {
						if (props.objectFields?.length > 0) {
							const result = {} as Record<keyof VTObj, unknown>;
							props.objectFields.forEach((field) => {
								result[field] = (item as Record<keyof VTObj, unknown>)[field];
							});
							return result;
						} else {
							return item;
						}
					});
				// selectedItems is OptionType (untouched passthrough) when objectFields is empty, or the
				// synthesized Record<keyof VTObj, unknown> otherwise - both are runtime-shaped like VTObj,
				// but that can't be proven generically since OptionType and VTObj are unrelated type params
				props.onChange?.(selectedItems as VTObj[], option);
			} else {
				props.onChange?.(value, option);
			}
		},
		[fields, props.onChange, props.valueAsObject, props.options],
	);

	const propsCopy = { ...props };
	if (propsCopy.valueAsObject == true) {
		delete propsCopy.objectFields;
	}

	// value/valueAsObject/onChange are pulled out only to keep them out of `rest` below -
	// value is replaced with processedValue and onChange with onChangeWrapper
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { value, valueAsObject, onChange, ...rest } = propsCopy;

	return {
		value: processedValue,
		onChange: onChangeWrapper,
		...rest,
	};
}
